import React, { useRef, useEffect } from 'react';

interface DualRangeSliderProps {
  min: number;
  max: number;
  minValue: number | string;
  maxValue: number | string;
  onChange: (minValue: number, maxValue: number) => void;
  label?: React.ReactNode;
}

const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min,
  max,
  minValue,
  maxValue,
  onChange,
  label,
}) => {
  const minValRef = useRef<HTMLInputElement>(null);
  const maxValRef = useRef<HTMLInputElement>(null);
  const range = useRef<HTMLDivElement>(null);

  const minVal = Number(minValue);
  const maxVal = Number(maxValue);

  useEffect(() => {
    if (range.current) {
      const minPercent = ((minVal - min) / (max - min)) * 100;
      const maxPercent = ((maxVal - min) / (max - min)) * 100;
      range.current.style.left = `${minPercent}%`;
      range.current.style.right = `${100 - maxPercent}%`;
    }
  }, [minVal, maxVal, min, max]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value), maxVal);
    onChange(newMin, maxVal);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value), minVal);
    onChange(minVal, newMax);
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-semibold text-gray-900">
          RM{minVal} - RM{maxVal}
        </div>
      </div>

      <div className="relative pt-2 pb-2">
        <div className="absolute top-1/2 transform -translate-y-1/2 w-full h-2 bg-gray-300 rounded-lg pointer-events-none" />
        <div
          ref={range}
          className="absolute top-1/2 transform -translate-y-1/2 h-2 bg-primary rounded-lg pointer-events-none"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={minVal}
          onChange={handleMinChange}
          ref={minValRef}
          className="absolute w-full h-2 top-1/2 transform -translate-y-1/2 pointer-events-none appearance-none bg-transparent cursor-pointer accent-primary"
          style={{ WebkitAppearance: 'none', zIndex: minVal >= maxVal - 1 ? 5 : 10 }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={maxVal}
          onChange={handleMaxChange}
          ref={maxValRef}
          className="absolute w-full h-2 top-1/2 transform -translate-y-1/2 pointer-events-none appearance-none bg-transparent cursor-pointer accent-primary"
          style={{ WebkitAppearance: 'none', zIndex: minVal >= maxVal - 1 ? 10 : 5 }}
        />
      </div>

      <style>{`
        input[type='range']::-webkit-slider-thumb {
          appearance: none;
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #2F5249;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          pointer-events: auto;
          z-index: 10;
        }
        input[type='range']::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #2F5249;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          pointer-events: auto;
          z-index: 10;
        }
        input[type='range']::-webkit-slider-thumb:hover {
          background: #1a3731;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
        input[type='range']::-moz-range-thumb:hover {
          background: #1a3731;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default DualRangeSlider;
